import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  TableRow,
  TableCell,
  Box,
  Stack,
  Avatar,
  Typography,
  Chip,
  Switch,
  Button,
  IconButton,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getRoleInfo } from './roleUtils';

const AdminTableRow = ({
  admin,
  getFullImageUrl,
  loadingId,
  onToggleAvailability,
  onConnectGoogle,
  onDisconnectGoogle,
  onOpenSessions,
  onOpenView,
  onOpenDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: admin.id_user });

  // Style indispensable pour le rendu fluide du Drag & Drop
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#F4F7FE' : 'inherit',
    zIndex: isDragging ? 999 : 'auto',
    position: 'relative',
  };

  const roleInfo = getRoleInfo(admin.role_user);
  
  // Rôles pris en charge (ex: 3 = intervenant, 4 = praticien)
  const roleId = Number(admin.role_user);
  const hasCalendarSupport = roleId === 3 || roleId === 4;

  const isConnected = admin.google_calendar_account?.is_connected;
  const isLoading = loadingId === admin.id_user;

  // 🔍 Affichage dans la console (optionnel, peut être retiré en prod)
  console.log("--- Debug Google Calendar & Rôles ---", {
    adminObject: admin,
    roleUserBrut: admin.role_user,
    roleIdNumber: roleId,
    roleInfo: roleInfo,
    hasCalendarSupport: hasCalendarSupport,
    googleCalendarAccount: admin.google_calendar_account,
    isConnected: isConnected
  });

  return (
    <TableRow ref={setNodeRef} style={style}>
      {/* HANDLE DRAG */}
      <TableCell width={50}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'grab',
            color: '#A3AED0',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        >
          <DragIndicatorIcon />
        </Box>
      </TableCell>

      {/* USER */}
      <TableCell>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar src={getFullImageUrl(admin.photo_user)} />
          <Box>
            <Typography sx={{ fontWeight: 600, color: '#2B3674' }}>
              {admin.nom_user} {admin.prenom_user}
            </Typography>
            <Typography variant="caption" sx={{ color: '#A3AED0' }}>
              {admin.email_user}
            </Typography>
          </Box>
        </Stack>
      </TableCell>

      {/* ROLE */}
      <TableCell>
        <Chip
          label={roleInfo.label}
          sx={{
            bgcolor: roleInfo.bgcolor,
            color: roleInfo.color,
            fontWeight: 700,
          }}
        />
      </TableCell>

      {/* STATUS */}
      <TableCell>
        <Switch
          checked={Number(admin.availability_user) === 1}
          onChange={() => onToggleAvailability(admin)}
        />
      </TableCell>

      {/* GOOGLE CALENDAR */}
      <TableCell>
        {hasCalendarSupport ? (
          isConnected ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleIcon color="success" fontSize="small" />
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={isLoading}
                onClick={() => onDisconnectGoogle(admin.id_user)}
                sx={{ textTransform: 'none', borderRadius: '8px' }}
              >
                Déconnecter
              </Button>
            </Stack>
          ) : (
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={isLoading}
              onClick={() => onConnectGoogle(admin.id_user)}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                whiteSpace: 'nowrap',
              }}
            >
              Connecter Google
            </Button>
          )
        ) : (
          <Typography variant="caption" color="textSecondary">
            -
          </Typography>
        )}
      </TableCell>

      {/* SESSIONS */}
      <TableCell>
        <Button 
          onClick={() => onOpenSessions(admin)}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Sessions
        </Button>
      </TableCell>

      {/* ACTIONS */}
      <TableCell align="right">
        <IconButton onClick={() => onOpenView(admin)} color="primary">
          <VisibilityIcon />
        </IconButton>
        <IconButton onClick={() => onOpenDelete(admin)} color="error">
          <DeleteOutlineIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

export default AdminTableRow;